import React, { useRef, useEffect, useState, useCallback } from "react";
import {
  Activity,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  Filter,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Zap,
  Shield,
  BarChart2,
  X,
} from "lucide-react";
import {
  Chart,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  ArcElement,
  DoughnutController,
  LineController,
  BarElement,
  BarController,
} from "chart.js";
import { useGetDashboardData } from "../../../useQuery";
import nodata from "../../../Assets/nodata.svg";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { GitBranch } from "lucide-react";

Chart.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  ArcElement,
  DoughnutController,
  LineController,
  BarElement,
  BarController,
);

const getMethod = (s) => {
  try {
    return JSON.parse(s).method || "UNKNOWN";
  } catch {
    return "UNKNOWN";
  }
};
const getUrl = (s) => {
  try {
    return JSON.parse(s).url || "";
  } catch {
    return "";
  }
};
const getPath = (url) => {
  try {
    const u = new URL(url);
    return u.origin + u.pathname;
  } catch {
    return url;
  }
};
const getMs = (s) => {
  try {
    const m = (JSON.parse(s).testResults?.["Response Time Check"] || "").match(/(\d+)\s*ms/);
    return m ? parseInt(m[1], 10) : null;
  } catch {
    return null;
  }
};
const getStatusCode = (s) => {
  try {
    const resp = JSON.parse(s);
    const orig = resp.originalResponse;
    if (orig?.status) return orig.status;
    const sc = resp.testResults?.["Status Code Check"] || "";
    const m = sc.match(/\b(\d{3})\b/);
    return m ? parseInt(m[1]) : null;
  } catch {
    return null;
  }
};
const getErrorMsg = (s) => {
  try {
    const resp = JSON.parse(s);
    const orig = resp.originalResponse;
    if (orig?.message) {
      try {
        const inner = JSON.parse(orig.message);
        return inner.message || orig.message;
      } catch {
        return orig.message;
      }
    }
    return null;
  } catch {
    return null;
  }
};
const getFailReason = (s) => {
  try {
    const results = JSON.parse(s).testResults || {};
    const failed = Object.entries(results).filter(([, v]) => v.startsWith("FAIL"));
    if (!failed.length) return null;
    return failed.map(([k]) => k.replace(" Check", "")).join(", ");
  } catch {
    return null;
  }
};
const relTime = (ts) => {
  const d = Math.floor((Date.now() - new Date(ts)) / 60000);
  if (d < 1) return "Just now";
  if (d < 60) return `${d}m ago`;
  if (d < 1440) return `${Math.floor(d / 60)}h ago`;
  return `${Math.floor(d / 1440)}d ago`;
};
const fmtDate = (d) => d.toISOString().slice(0, 10);

const localDateKey = (d) => {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function useChart(ref, buildFn, deps) {
  const instanceRef = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!ref.current) return;
    instanceRef.current?.destroy();
    instanceRef.current = buildFn(ref.current);
    return () => {
      instanceRef.current?.destroy();
    };
  }, [buildFn, ref, deps]);
}

const METHOD_STYLES = {
  GET: { bg: "bg-sky-100", text: "text-sky-700", border: "border-sky-200", dot: "bg-sky-500" },
  POST: {
    bg: "bg-emerald-100",
    text: "text-emerald-700",
    border: "border-emerald-200",
    dot: "bg-emerald-500",
  },
  PUT: {
    bg: "bg-amber-100",
    text: "text-amber-700",
    border: "border-amber-200",
    dot: "bg-amber-500",
  },
  DELETE: {
    bg: "bg-rose-100",
    text: "text-rose-700",
    border: "border-rose-200",
    dot: "bg-rose-500",
  },
};

const MethodBadge = ({ method }) => {
  const s = METHOD_STYLES[method] || {
    bg: "bg-gray-100",
    text: "text-gray-600",
    border: "border-gray-200",
    dot: "bg-gray-400",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold font-mono border ${s.bg} ${s.text} ${s.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {method}
    </span>
  );
};

const StatusPill = ({ status }) => {
  const pass = status === "PASS";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide ${pass ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${pass ? "bg-emerald-500" : "bg-red-500"} animate-pulse`}
      />
      {status}
    </span>
  );
};

const StatusCodeBadge = ({ code }) => {
  if (!code) return <span className="text-gray-400 text-xs">—</span>;
  const c = parseInt(code);
  const cls =
    c >= 500
      ? "bg-red-100 text-red-700 border-red-200"
      : c >= 400
        ? "bg-orange-100 text-orange-700 border-orange-200"
        : c >= 300
          ? "bg-blue-100 text-blue-700 border-blue-200"
          : "bg-emerald-100 text-emerald-700 border-emerald-200";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-bold font-mono ${cls}`}
    >
      {code}
    </span>
  );
};

const Card = ({ children, className = "" }) => (
  <div
    className={`bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 ${className}`}
  >
    {children}
  </div>
);

const CardHeader = ({ title, subtitle, action, icon }) => (
  <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-50">
    <div className="flex items-center gap-2.5">
      {icon && <span className="text-gray-400">{icon}</span>}
      <div>
        <p className="text-[13px] font-semibold text-gray-800 tracking-tight">{title}</p>
        {subtitle && <p className="text-[10px] text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {action && <div>{action}</div>}
  </div>
);

const ROWS_PER_PAGE = 8;

const FailedTestsModal = ({ allFailed, onClose }) => {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(allFailed.length / ROWS_PER_PAGE);
  const slice = allFailed.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden border border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-rose-50/40 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-rose-100 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-rose-500" />
            </div>
            <div>
              <p className="text-[14px] font-bold text-gray-900 tracking-tight">All Failed Tests</p>
              <p className="text-[10px] text-gray-400 font-medium">
                {allFailed.length} total failure{allFailed.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-auto flex-1">
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-50 border-b border-gray-100">
                {[
                  "Method",
                  "Endpoint",
                  "Status Code",
                  "Response Time",
                  "Fail Reason",
                  "Failed At",
                ].map((col) => (
                  <th
                    key={col}
                    className="px-5 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-widest whitespace-nowrap"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {slice.map((h, i) => {
                const m = getMethod(h.apiRequest);
                const ep = getPath(getUrl(h.apiRequest));
                const ms = getMs(h.apiResponse);
                const code = getStatusCode(h.apiResponse);
                const errMsg = getErrorMsg(h.apiResponse);
                const failReason = getFailReason(h.apiResponse);
                return (
                  <tr key={i} className="hover:bg-rose-50/30 transition-colors">
                    <td className="px-5 py-3.5">
                      <MethodBadge method={m} />
                    </td>
                    <td className="px-5 py-3.5 max-w-[200px]">
                      <span
                        className="font-mono text-[11px] text-gray-700 truncate block"
                        title={ep}
                      >
                        {ep}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <StatusCodeBadge code={code} />
                    </td>
                    <td className="px-5 py-3.5">
                      {ms !== null ? (
                        <span
                          className={`font-mono text-[11px] font-bold px-2 py-0.5 rounded-md ${ms > 2000 ? "bg-red-100 text-red-600" : ms > 1000 ? "bg-amber-100 text-amber-600" : "bg-gray-100 text-gray-600"}`}
                        >
                          {ms}ms
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 max-w-[260px]">
                      {failReason ? (
                        <span
                          className="text-[11px] text-rose-600 font-medium truncate block"
                          title={failReason}
                        >
                          {failReason}
                        </span>
                      ) : errMsg ? (
                        <span className="text-[11px] text-gray-500 truncate block" title={errMsg}>
                          {errMsg}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[11px] text-gray-700 font-semibold">
                          {relTime(h.timestamp)}
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {new Date(h.timestamp).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-6 py-3.5 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
          <p className="text-[11px] text-gray-500 font-medium">
            Showing{" "}
            <span className="font-bold text-gray-700">
              {(page - 1) * ROWS_PER_PAGE + 1}–{Math.min(page * ROWS_PER_PAGE, allFailed.length)}
            </span>{" "}
            of <span className="font-bold text-gray-700">{allFailed.length}</span> failures
          </p>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-7 h-7 rounded-lg text-[11px] font-bold border transition-all ${page === p ? "bg-indigo-600 text-white border-indigo-600 shadow-sm" : "border-gray-200 text-gray-500 hover:border-indigo-300 hover:text-indigo-600"}`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ApiDashboard = () => {
  const today = new Date();
  const d7ago = new Date(today);
  d7ago.setDate(today.getDate() - 6);

  const [dateFrom, setDateFrom] = useState(fmtDate(d7ago));
  const [dateTo, setDateTo] = useState(fmtDate(today));
  const [method, setMethod] = useState("ALL");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);

  const { data, isLoading } = useGetDashboardData({
    dateFrom,
    dateTo,
    method: method !== "ALL" ? method : undefined,
  });

  const dashboardData = data?.dashboardData;
  const rawHistory = data?.latestHistory || [];

  const history = rawHistory.filter((h) => {
    if (method !== "ALL" && getMethod(h.apiRequest) !== method) return false;
    const ts = new Date(h.timestamp);
    if (dateFrom && ts < new Date(dateFrom)) return false;
    if (dateTo && ts > new Date(dateTo + "T23:59:59")) return false;
    return true;
  });

  const totalCalls = dashboardData?.totalApiCalls || 0;
  const successRate = dashboardData?.successRate || 0;
  const avgResp = dashboardData?.avgResponseTime || 0;
  const failedReqs = dashboardData?.failedRequests || 0;
  const totalEndpoints = dashboardData?.totalEndpoints || 0;
  const neverExecutedEndpoints = dashboardData?.neverExecutedEndpoints || 0;
  const passedCalls = Math.round(totalCalls * successRate);

  const slowest = [...history]
    .map((h) => ({
      method: getMethod(h.apiRequest),
      endpoint: getPath(getUrl(h.apiRequest)),
      ms: getMs(h.apiResponse) || 0,
    }))
    .sort((a, b) => b.ms - a.ms)
    .slice(0, 5);
  const maxMs = slowest[0]?.ms || 1;

  const failingMap = {};
  history.forEach((h) => {
    const ep = getPath(getUrl(h.apiRequest));
    if (!failingMap[ep])
      failingMap[ep] = { endpoint: ep, method: getMethod(h.apiRequest), fail: 0, total: 0 };
    failingMap[ep].total++;
    if (h.status === "FAIL") failingMap[ep].fail++;
  });
  const topFailing = Object.values(failingMap)
    .filter((e) => e.fail > 0)
    .sort((a, b) => b.fail - a.fail)
    .slice(0, 5);
  const endpointRates = Object.values(failingMap)
    .sort((a, b) => b.total - a.total)
    .slice(0, 6);

  const recentFailed = history.filter((h) => h.status === "FAIL").slice(0, 8);

  const chartDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (6 - i));
    return localDateKey(d);
  });

  const trendMap = {};
  chartDays.forEach((d) => {
    trendMap[d] = { pass: 0, fail: 0 };
  });
  rawHistory.forEach((h) => {
    const key = localDateKey(h.timestamp);
    if (trendMap[key]) {
      h.status === "PASS" ? trendMap[key].pass++ : trendMap[key].fail++;
    }
  });

  const trendLabels = chartDays.map((d) => {
    const [y, m, day] = d.split("-").map(Number);
    return new Date(y, m - 1, day).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  });
  const trendPassed = chartDays.map((d) => trendMap[d].pass);
  const trendFailed2 = chartDays.map((d) => trendMap[d].fail);
  const hasMultiDayData =
    chartDays.filter((d) => trendMap[d].pass + trendMap[d].fail > 0).length > 1;

  const respMap = {};
  chartDays.forEach((d) => {
    respMap[d] = [];
  });
  rawHistory.forEach((h) => {
    const key = localDateKey(h.timestamp);
    const ms = getMs(h.apiResponse);
    if (respMap[key] && ms !== null) respMap[key].push(ms);
  });
  const respData = chartDays.map((d) => {
    const arr = respMap[d];
    return arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : null;
  });

  const trendRef = useRef(null);
  const donutRef = useRef(null);
  const respRef = useRef(null);
  const rateRef = useRef(null);

  const buildTrendChart = useCallback(
    (canvas) =>
      new Chart(canvas, {
        type: "line",
        data: {
          labels: trendLabels,
          datasets: [
            {
              label: "Passed",
              data: trendPassed,
              borderColor: "#10b981",
              backgroundColor: "rgba(16,185,129,0.08)",
              borderWidth: 2,
              pointRadius: (ctx) => (trendPassed[ctx.dataIndex] > 0 ? 5 : 3),
              pointHoverRadius: 6,
              pointBackgroundColor: "#10b981",
              tension: 0.3,
              fill: true,
            },
            {
              label: "Failed",
              data: trendFailed2,
              borderColor: "#f43f5e",
              backgroundColor: "rgba(244,63,94,0.06)",
              borderWidth: 2,
              pointRadius: (ctx) => (trendFailed2[ctx.dataIndex] > 0 ? 5 : 3),
              pointHoverRadius: 6,
              pointBackgroundColor: "#f43f5e",
              tension: 0.3,
              fill: true,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: "index",
              intersect: false,
              backgroundColor: "#1e293b",
              titleFont: { size: 11 },
              bodyFont: { size: 11 },
            },
          },
          scales: {
            x: { grid: { display: false }, ticks: { font: { size: 10 }, color: "#94a3b8" } },
            y: {
              grid: { color: "#f1f5f9" },
              ticks: { font: { size: 10 }, color: "#94a3b8", precision: 0 },
              beginAtZero: true,
              grace: "15%",
            },
          },
        },
      }),
    [trendLabels, trendPassed, trendFailed2],
  );

  const buildDonutChart = useCallback(
    (canvas) =>
      new Chart(canvas, {
        type: "doughnut",
        data: {
          datasets: [
            {
              data: [passedCalls || 1140, failedReqs || 707],
              backgroundColor: ["#6366f1", "#f43f5e"],
              borderWidth: 0,
              hoverOffset: 6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "76%",
          plugins: { legend: { display: false } },
        },
      }),
    [passedCalls, failedReqs],
  );

  const buildRespChart = useCallback(
    (canvas) => {
      const validResp = respData.filter((v) => v !== null);
      const minResp = validResp.length ? Math.min(...validResp) : 0;
      const maxResp = validResp.length ? Math.max(...validResp) : 1000;
      const yMin = Math.max(0, minResp - Math.round(minResp * 0.15));
      const yMax = maxResp + Math.round(maxResp * 0.15);
      const isBar = !hasMultiDayData;
      return new Chart(canvas, {
        type: isBar ? "bar" : "line",
        data: {
          labels: trendLabels,
          datasets: [
            {
              data: isBar ? respData.map((v) => v ?? 0) : respData,
              borderColor: "#8b5cf6",
              backgroundColor: isBar ? "rgba(139,92,246,0.6)" : "rgba(139,92,246,0.08)",
              borderWidth: isBar ? 0 : 2,
              borderRadius: isBar ? 6 : undefined,
              pointRadius: isBar ? undefined : (ctx) => (respData[ctx.dataIndex] !== null ? 4 : 0),
              pointHoverRadius: isBar ? undefined : 6,
              pointBackgroundColor: "#8b5cf6",
              tension: 0.3,
              fill: !isBar,
              spanGaps: true,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: "index",
              intersect: false,
              backgroundColor: "#1e293b",
              callbacks: {
                label: (ctx) => (ctx.parsed.y ? `${ctx.parsed.y}ms` : "No data"),
              },
            },
          },
          scales: {
            x: { grid: { display: false }, ticks: { font: { size: 10 }, color: "#94a3b8" } },
            y: {
              grid: { color: "#f1f5f9" },
              ticks: { font: { size: 10 }, color: "#94a3b8", callback: (v) => `${v}ms` },
              beginAtZero: false,
              min: isBar ? Math.max(0, yMin) : Math.max(0, yMin),
              max: yMax,
            },
          },
        },
      });
    },
    [trendLabels, respData, hasMultiDayData],
  );

  const buildRateChart = useCallback(
    (canvas) =>
      new Chart(canvas, {
        type: "bar",
        data: {
          labels: endpointRates.map((e) => e.endpoint.split("/").slice(-2).join("/")),
          datasets: [
            {
              label: "Pass",
              data: endpointRates.map((e) => e.total - e.fail),
              backgroundColor: "rgba(16,185,129,0.75)",
              borderRadius: 4,
              borderSkipped: false,
            },
            {
              label: "Fail",
              data: endpointRates.map((e) => e.fail),
              backgroundColor: "rgba(244,63,94,0.75)",
              borderRadius: 4,
              borderSkipped: false,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: "y",
          plugins: {
            legend: { display: false },
            tooltip: { mode: "index", intersect: false, backgroundColor: "#1e293b" },
          },
          scales: {
            x: {
              stacked: true,
              grid: { color: "#f1f5f9" },
              ticks: { font: { size: 10 }, color: "#94a3b8" },
            },
            y: {
              stacked: true,
              grid: { display: false },
              ticks: { font: { size: 10 }, color: "#64748b" },
            },
          },
        },
      }),
    [endpointRates],
  );

  useChart(trendRef, buildTrendChart, [buildTrendChart]);
  useChart(donutRef, buildDonutChart, [buildDonutChart]);
  useChart(respRef, buildRespChart, [buildRespChart]);
  useChart(rateRef, buildRateChart, [buildRateChart]);

  if (isLoading)
    return (
      <div className="bg-slate-50 h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-indigo-500 border-t-transparent" />
            <div className="absolute inset-0 animate-ping rounded-full h-10 w-10 border border-indigo-300 opacity-30" />
          </div>
          <span className="text-slate-400 text-sm font-medium tracking-wide">
            Loading dashboard…
          </span>
        </div>
      </div>
    );

  if (!data || (!rawHistory.length && !dashboardData))
    return (
      <div className="bg-slate-50 h-full flex items-center justify-center">
        <img src={nodata} alt="No data" className="w-72 md:w-96 h-auto opacity-70" />
      </div>
    );

  // Add this BEFORE statCards (after passedCalls line)
  const now = Date.now();
  const msIn7Days = 7 * 24 * 60 * 60 * 1000;
  const cutoff = now - msIn7Days / 2; // midpoint = 3.5 days ago

  const prevHalf = rawHistory.filter((h) => new Date(h.timestamp).getTime() < cutoff);
  const currHalf = rawHistory.filter((h) => new Date(h.timestamp).getTime() >= cutoff);

  const calcDelta = (curr, prev) => {
    if (!prev || prev === 0) return null;
    const pct = (((curr - prev) / prev) * 100).toFixed(1);
    return pct;
  };

  const prevCalls = prevHalf.length;
  const currCalls = currHalf.length;
  const callsDelta = calcDelta(currCalls, prevCalls);

  const prevPassed = prevHalf.filter((h) => h.status === "PASS").length;
  const currPassed = currHalf.filter((h) => h.status === "PASS").length;
  const prevRate = prevHalf.length ? prevPassed / prevHalf.length : 0;
  const currRate = currHalf.length ? currPassed / currHalf.length : 0;
  const rateDelta = calcDelta(currRate, prevRate);

  const getAvgMs = (arr) => {
    const times = arr.map((h) => getMs(h.apiResponse)).filter((v) => v !== null);
    return times.length ? times.reduce((a, b) => a + b, 0) / times.length : null;
  };
  const prevAvgMs = getAvgMs(prevHalf);
  const currAvgMs = getAvgMs(currHalf);
  const respDelta = calcDelta(currAvgMs, prevAvgMs);

  const fmtDelta = (delta, lowerIsBetter = false) => {
    if (delta === null) return { text: "Not enough data", up: null };
    const num = parseFloat(delta);
    const up = lowerIsBetter ? num < 0 : num > 0;
    const arrow = num > 0 ? "↑" : "↓";
    return { text: `${arrow} ${Math.abs(num)}% vs prior period`, up };
  };

  const callsDeltaFmt = fmtDelta(callsDelta);
  const rateDeltaFmt = fmtDelta(rateDelta);
  const respDeltaFmt = fmtDelta(respDelta, true);

  const statCards = [
    {
      label: "Total API Calls",
      value: totalCalls.toLocaleString(),
      delta: callsDeltaFmt.text,
      up: callsDeltaFmt.up ?? true,
      icon: <Activity className="w-4 h-4" />,
      iconBg: "bg-blue-50",
      iconColor: "text-blue-500",
      accentFrom: "from-blue-400",
      accentTo: "to-blue-600",
      glow: "shadow-blue-100",
    },
    {
      label: "Success Rate",
      value: `${(successRate * 100).toFixed(1)}%`,
      delta: rateDeltaFmt.text,
      up: rateDeltaFmt.up ?? true,
      icon: <CheckCircle className="w-4 h-4" />,
      iconBg: "bg-emerald-50",
      iconColor: "text-emerald-500",
      accentFrom: "from-emerald-400",
      accentTo: "to-emerald-600",
      glow: "shadow-emerald-100",
    },
    {
      label: "Avg Response Time",
      value: `${avgResp.toLocaleString()}ms`,
      delta: respDeltaFmt.text,
      up: respDeltaFmt.up ?? false,
      icon: <Zap className="w-4 h-4" />,
      iconBg: "bg-amber-50",
      iconColor: "text-amber-500",
      accentFrom: "from-amber-400",
      accentTo: "to-orange-500",
      glow: "shadow-amber-100",
    },
    {
      label: "Total Endpoints",
      value: totalEndpoints.toLocaleString(),
      delta: "Monitored API endpoints",
      up: true,
      icon: <GitBranch className="w-4 h-4" />,
      iconBg: "bg-violet-50",
      iconColor: "text-violet-500",
      accentFrom: "from-violet-400",
      accentTo: "to-violet-600",
      glow: "shadow-violet-100",
    },
    {
      label: "Never Executed",
      value: neverExecutedEndpoints.toLocaleString(),
      delta: "APIs with zero executions",
      up: false,
      icon: <AlertTriangle className="w-4 h-4" />,
      iconBg: "bg-rose-50",
      iconColor: "text-rose-500",
      accentFrom: "from-rose-400",
      accentTo: "to-rose-600",
      glow: "shadow-rose-100",
    },
  ];

  const handleDownloadPDF = async () => {
    const btn = document.getElementById("download-btn");
    btn.disabled = true;
    btn.textContent = "Generating...";

    try {
      const dashboard = document.getElementById("dashboard-content");

      // Collect all ancestors and force-override Tailwind classes with !important
      const ancestors = [];
      let el = dashboard.parentElement;
      while (el && el !== document.body) {
        ancestors.push({ el, cssText: el.style.cssText });
        el.style.cssText +=
          "; overflow: visible !important; height: auto !important; max-height: none !important;";
        el = el.parentElement;
      }

      const origCssText = dashboard.style.cssText;
      dashboard.style.cssText +=
        "; overflow: visible !important; height: auto !important; max-height: none !important;";

      await new Promise((r) => setTimeout(r, 500));

      const fullHeight = dashboard.scrollHeight;
      const fullWidth = dashboard.offsetWidth;

      const canvas = await html2canvas(dashboard, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#f8fafc",
        scrollY: 0,
        scrollX: 0,
        width: fullWidth,
        height: fullHeight,
        windowWidth: fullWidth,
        windowHeight: fullHeight,
      });

      const imgData = canvas.toDataURL("image/png");

      // Restore all styles
      dashboard.style.cssText = origCssText;
      ancestors.forEach(({ el, cssText }) => {
        el.style.cssText = cssText;
      });

      const imgW = canvas.width / 2;
      const imgH = canvas.height / 2;
      const headerH = 36;
      const footerH = 20;
      const pdfW = imgW;
      const pdfH = imgH + headerH + footerH;

      const pdf = new jsPDF({
        orientation: pdfW > pdfH ? "landscape" : "portrait",
        unit: "px",
        format: [pdfW, pdfH],
      });

      pdf.setFillColor(79, 70, 229);
      pdf.rect(0, 0, pdfW, headerH, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.text("API Dashboard Report", 20, 24);
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.text(`Generated: ${new Date().toLocaleString()}`, pdfW - 20, 24, { align: "right" });

      pdf.addImage(imgData, "PNG", 0, headerH, imgW, imgH);

      pdf.setFillColor(248, 250, 252);
      pdf.rect(0, pdfH - footerH, pdfW, footerH, "F");
      pdf.setTextColor(148, 163, 184);
      pdf.setFontSize(8);
      pdf.text("Confidential · API Monitoring Report", pdfW / 2, pdfH - 8, { align: "center" });

      pdf.save(`api-dashboard-report-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error(err);
    } finally {
      btn.disabled = false;
      btn.textContent = "Download Report";
    }
  };

  return (
    <div className="bg-[#f8fafc] h-full max-h-screen overflow-hidden flex flex-col font-sans">
      <div className="bg-white border-b border-gray-100 px-6 py-3.5 flex items-center justify-between flex-shrink-0 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shadow-lg shadow-indigo-200">
            <BarChart2 className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-[14px] font-bold text-gray-900 tracking-tight">API Dashboard</h1>
            <p className="text-[10px] text-gray-400 mt-0.5 font-medium">
              Monitor performance &amp; usage statistics
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-lg border border-emerald-100">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[10.5px] font-semibold text-emerald-700">Live</span>
          </div>
          <button
            onClick={() => setFiltersOpen((p) => !p)}
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${filtersOpen ? "bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-200" : "bg-white border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600"}`}
          >
            <Filter className="w-3.5 h-3.5" />
            Filters
            {method !== "ALL" && (
              <span
                className={`w-1.5 h-1.5 rounded-full ${filtersOpen ? "bg-white" : "bg-indigo-500"}`}
              />
            )}
          </button>
          <button
            id="download-btn"
            onClick={handleDownloadPDF}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            ⬇ Download Report
          </button>
        </div>
      </div>

      {filtersOpen && (
        <div className="bg-white border-b border-gray-100 px-6 py-3 flex flex-wrap items-center gap-4 flex-shrink-0">
          {[
            ["From", dateFrom, (v) => setDateFrom(v), { max: dateTo }],
            ["To", dateTo, (v) => setDateTo(v), { min: dateFrom }],
          ].map(([label, val, setter, extra]) => (
            <div key={label} className="flex items-center gap-2">
              <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                {label}
              </label>
              <input
                type="date"
                value={val}
                {...extra}
                onChange={(e) => setter(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-gray-50"
              />
            </div>
          ))}
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
              Method
            </label>
            <div className="flex items-center gap-1">
              {["ALL", "GET", "POST", "PUT", "DELETE"].map((m) => (
                <button
                  key={m}
                  onClick={() => setMethod(m)}
                  className={`text-[10px] font-bold px-2.5 py-1 rounded-md border transition-all ${method === m ? "bg-indigo-600 text-white border-indigo-600 shadow-sm" : "bg-white text-gray-500 border-gray-200 hover:border-indigo-300 hover:text-indigo-600"}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => {
              setDateFrom(fmtDate(d7ago));
              setDateTo(fmtDate(today));
              setMethod("ALL");
            }}
            className="text-[11px] text-gray-400 hover:text-red-500 ml-auto transition-colors font-semibold"
          >
            Reset
          </button>
        </div>
      )}

      <div id="dashboard-content" className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        <div className="grid grid-cols-6 gap-3">
          {statCards.map((s, i) => (
            <div
              key={i}
              className={`bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-lg ${s.glow} transition-all duration-200 cursor-default relative overflow-hidden`}
            >
              <div
                className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${s.accentFrom} ${s.accentTo} rounded-t-2xl`}
              />
              <div
                className={`w-9 h-9 rounded-xl ${s.iconBg} ${s.iconColor} flex items-center justify-center mb-4`}
              >
                {s.icon}
              </div>
              <p className="text-[10px] font-bold text-gray-400 mb-1.5 uppercase tracking-widest">
                {s.label}
              </p>
              <p className="text-[28px] font-black text-gray-900 tracking-tight leading-none">
                {s.value}
              </p>
              <p
                className={`text-[10.5px] mt-2.5 font-semibold flex items-center gap-1 ${s.up ? "text-emerald-600" : "text-rose-500"}`}
              >
                {s.up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {s.delta}
              </p>
            </div>
          ))}

          <Card>
            <CardHeader
              title="Status Distribution"
              icon={<CheckCircle className="w-3.5 h-3.5" />}
            />
            <div className="p-4 flex flex-col items-center">
              <div className="relative w-28 h-28">
                <canvas ref={donutRef} role="img" aria-label="Status donut chart" />
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <p className="text-lg font-black text-gray-900 leading-tight">
                    {totalCalls.toLocaleString()}
                  </p>
                  <p className="text-[8px] text-gray-400 font-bold uppercase tracking-widest">
                    Total
                  </p>
                </div>
              </div>
              <div className="mt-3 w-full space-y-2">
                {[
                  {
                    label: "Passed",
                    val: passedCalls,
                    pct: Math.round(successRate * 100),
                    color: "bg-indigo-500",
                  },
                  {
                    label: "Failed",
                    val: failedReqs,
                    pct: Math.round((1 - successRate) * 100),
                    color: "bg-rose-400",
                  },
                ].map((l) => (
                  <div key={l.label} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 text-gray-500 font-medium">
                      <span className={`w-2 h-2 rounded-sm ${l.color}`} />
                      {l.label}
                    </span>
                    <span className="font-bold text-gray-800">
                      {l.val.toLocaleString()}{" "}
                      <span className="text-gray-400 font-normal">({l.pct}%)</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Card>
            <CardHeader
              title="Test Execution Trend"
              action={
                <div className="flex items-center gap-4 text-[10px] text-gray-400 font-medium">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-sm bg-emerald-400" />
                    Passed
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-sm bg-rose-400" />
                    Failed
                  </span>
                </div>
              }
            />
            <div className="p-5" style={{ height: 190 }}>
              <canvas ref={trendRef} role="img" aria-label="Test execution trend" />
            </div>
          </Card>
          <Card>
            <CardHeader
              title="Response Time (ms)"
              action={
                <span className="text-[10.5px] text-purple-500 font-semibold">7-day avg</span>
              }
            />
            <div className="p-5" style={{ height: 190 }}>
              <canvas ref={respRef} role="img" aria-label="Response time area chart" />
            </div>
          </Card>
        </div>

        <div className="grid gap-3" style={{ gridTemplateColumns: "1fr 1.2fr 1fr" }}>
          <Card>
            <CardHeader
              title="Top Failing Endpoints"
              icon={<AlertTriangle className="w-3.5 h-3.5 text-amber-400" />}
            />
            <div className="px-4 py-2 divide-y divide-gray-50">
              {topFailing.length > 0 ? (
                topFailing.map((ep, i) => (
                  <div key={i} className="flex items-center gap-3 py-3">
                    <MethodBadge method={ep.method} />
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-[11px] font-mono text-gray-600 truncate"
                        title={ep.endpoint}
                      >
                        {ep.endpoint}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-rose-400 to-red-500 rounded-full"
                            style={{ width: `${(ep.fail / ep.total) * 100}%` }}
                          />
                        </div>
                        <span className="text-[10px] font-bold text-rose-500 whitespace-nowrap">
                          {ep.fail}/{ep.total}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-8 text-center">
                  <CheckCircle className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-400">No failures found</p>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Top Slowest Endpoints"
              action={
                <span className="text-[10px] text-gray-400 font-medium">by response time</span>
              }
            />
            <div className="px-4 py-2 divide-y divide-gray-50">
              {slowest.length > 0 ? (
                slowest.map((ep, i) => (
                  <div key={i} className="flex items-center gap-3 py-3">
                    <MethodBadge method={ep.method} />
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-[11px] font-mono text-gray-600 truncate"
                        title={ep.endpoint}
                      >
                        {ep.endpoint}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-amber-400 to-rose-400 rounded-full"
                            style={{ width: `${(ep.ms / maxMs) * 100}%` }}
                          />
                        </div>
                        <span className="text-[10px] font-bold text-gray-600 whitespace-nowrap">
                          {ep.ms}ms
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-8 text-center">
                  <CheckCircle className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-400">No data</p>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Recent API Activity" subtitle="Real-time request monitoring" />
            <div className="divide-y divide-gray-50">
              {history.slice(0, 8).map((h, i) => {
                const ep = getPath(getUrl(h.apiRequest));
                const m = getMethod(h.apiRequest);
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50/60 transition-colors"
                  >
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <MethodBadge method={m} />
                      <span
                        className="text-[11px] font-mono text-gray-600 truncate flex-1 min-w-0"
                        title={ep}
                      >
                        {ep}
                      </span>
                    </div>
                    <StatusPill status={h.status} />
                  </div>
                );
              })}
              {history.length === 0 && (
                <p className="text-center text-gray-400 text-sm py-10">
                  No activity matching current filters
                </p>
              )}
            </div>
          </Card>
        </div>

        <Card>
          <CardHeader
            title="Recent Failed Tests"
            subtitle={`${recentFailed.length} failure${recentFailed.length !== 1 ? "s" : ""} detected`}
            icon={<AlertTriangle className="w-3.5 h-3.5 text-rose-400" />}
            action={
              recentFailed.length > 0 ? (
                <button
                  onClick={() => setShowFailedModal(true)}
                  className="text-[10.5px] text-indigo-500 font-semibold flex items-center gap-0.5 hover:text-indigo-700 transition-colors"
                >
                  View all <ChevronRight className="w-3 h-3" />
                </button>
              ) : null
            }
          />
          {recentFailed.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-rose-50/40 border-b border-gray-100">
                    {[
                      "Method",
                      "Endpoint",
                      "Status Code",
                      "Response Time",
                      "Fail Reason",
                      "Failed At",
                    ].map((col) => (
                      <th
                        key={col}
                        className="px-5 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-widest whitespace-nowrap"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {recentFailed.slice(0, 5).map((h, i) => {
                    const m = getMethod(h.apiRequest);
                    const ep = getPath(getUrl(h.apiRequest));
                    const ms = getMs(h.apiResponse);
                    const code = getStatusCode(h.apiResponse);
                    const errMsg = getErrorMsg(h.apiResponse);
                    const failReason = getFailReason(h.apiResponse);
                    return (
                      <tr key={i} className="hover:bg-rose-50/30 transition-colors">
                        <td className="px-5 py-3">
                          <MethodBadge method={m} />
                        </td>
                        <td className="px-5 py-3 max-w-[180px]">
                          <span
                            className="font-mono text-[11px] text-gray-700 truncate block"
                            title={ep}
                          >
                            {ep}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <StatusCodeBadge code={code} />
                        </td>
                        <td className="px-5 py-3">
                          {ms !== null ? (
                            <span
                              className={`font-mono text-[11px] font-bold px-2 py-0.5 rounded-md ${ms > 2000 ? "bg-red-100 text-red-600" : ms > 1000 ? "bg-amber-100 text-amber-600" : "bg-gray-100 text-gray-600"}`}
                            >
                              {ms}ms
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3 max-w-[240px]">
                          {failReason ? (
                            <span
                              className="text-[11px] text-rose-600 font-medium truncate block"
                              title={failReason}
                            >
                              {failReason}
                            </span>
                          ) : errMsg ? (
                            <span
                              className="text-[11px] text-gray-500 truncate block"
                              title={errMsg}
                            >
                              {errMsg}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-[11px] text-gray-700 font-medium">
                              {relTime(h.timestamp)}
                            </span>
                            <span className="text-[10px] text-gray-400">
                              {new Date(h.timestamp).toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {recentFailed.length > 5 && (
                <div className="px-5 py-3 border-t border-gray-50 bg-gray-50/30">
                  <button
                    onClick={() => setShowFailedModal(true)}
                    className="text-[11px] text-indigo-500 font-semibold hover:text-indigo-700 transition-colors flex items-center gap-1"
                  >
                    +{recentFailed.length - 5} more failures — View all{" "}
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="py-12 text-center">
              <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-6 h-6 text-emerald-400" />
              </div>
              <p className="text-sm font-semibold text-gray-400">No recent failures</p>
              <p className="text-xs text-gray-300 mt-1">
                All tests passing within the selected range
              </p>
            </div>
          )}
        </Card>
      </div>

      {showFailedModal && (
        <FailedTestsModal
          allFailed={history.filter((h) => h.status === "FAIL")}
          onClose={() => setShowFailedModal(false)}
        />
      )}
    </div>
  );
};

export default ApiDashboard;
